import styled from 'styled-components'
import { Grid } from '../../../components/Grid'
import { ComponentLayout } from '../../../components/layouts/ComponentLayout'
import { BodyLarge, Title2 } from '../../../components/styles/Typography'
import { theme } from '../../../theme/theme'
import { TrainingCard } from '../../../components/TrainingCard'
import { useGetEntriesByContentType } from '../../../api/contentful/contentfulQueries'
import { LoadingSpinner } from '../../../components/LoadingSpinner'
import { PreviewGuard } from '../../../components/PreviewGuard'
import { useGetUserTrainingsQuery } from '../../../api/ester-b2b-api/private/trainings/trainingQueries'
import { TrainingStatus, TrainingType } from '../../../api/ester-b2b-api/private/trainings/types'
import { ITrainingFields } from '@estercare/ester-shared'
import { useSelectIsAdmin, useSelectIsAthlete, useSelectUserIsManager } from '../../../state/selectors'
import { useTranslation } from 'react-i18next'

export const TrainingIndex = () => {
  const { t } = useTranslation()
  const { data: trainings, isLoading: trainingsLoading } = useGetEntriesByContentType<ITrainingFields>('training')
  const { data: completedTrainings, isLoading: completedTrainingsLoading } = useGetUserTrainingsQuery()
  const isManager = useSelectUserIsManager()
  const isAthlete = useSelectIsAthlete()
  const isAdmin = useSelectIsAdmin()
  const showManagerTrainings = isManager || isAdmin

  if (trainingsLoading || completedTrainingsLoading) return <LoadingSpinner />
  if (!trainings?.items) return <PreviewGuard />

  const isTrainingCompleted = (trainingIdentifier: string) => {
    return Array.isArray(completedTrainings)
      ? completedTrainings.some(
          (training) => training.trainingId === trainingIdentifier && training.status === TrainingStatus.COMPLETED
        )
      : false
  }

  const sortedTrainings = () => {
    return trainings.items.filter((training) => {
      const identifier = training.fields.trainingIdentifier

      if (isAthlete && showManagerTrainings) {
        return (
          identifier === TrainingType.SPORTS_MANAGER_TRAINING ||
          identifier === TrainingType.SPORTS_WOMENS_HEALTH_TRAINING
        )
      }

      if (showManagerTrainings && !isAthlete) {
        return identifier === TrainingType.MANAGER_TRAINING || identifier === TrainingType.WOMENS_HEALTH_TRAINING
      }

      if (isAthlete && !showManagerTrainings) {
        return identifier === TrainingType.SPORTS_WOMENS_HEALTH_TRAINING
      }
      if (!isAthlete && !showManagerTrainings) {
        return identifier === TrainingType.WOMENS_HEALTH_TRAINING
      }
      return identifier === TrainingType.WOMENS_HEALTH_TRAINING
    })
  }
  return (
    <ComponentLayout>
      <StyledTitle2>{t('employee.trainings.title')}</StyledTitle2>
      <StyledBodyLarge>{t('employee.trainings.description')}</StyledBodyLarge>
      <Grid $columns={2}>
        {sortedTrainings().map(({ sys, fields: { slug, image, title, description, trainingIdentifier, duration } }) => {
          if (!title || !description || !trainingIdentifier || !slug || !image) {
            return <PreviewGuard key={sys.id} />
          }
          return (
            <TrainingCard
              key={sys.id}
              url={slug}
              imageUrl={image.fields.file.url}
              title={title}
              description={description}
              duration={Number(duration)}
              completed={isTrainingCompleted(trainingIdentifier)}
            />
          )
        })}
      </Grid>
    </ComponentLayout>
  )
}

const StyledTitle2 = styled(Title2)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
const StyledBodyLarge = styled(BodyLarge)`
  margin-bottom: ${theme.spacing.large}px;
  max-width: 640px;
`
